import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import themesFile from '@shared/themes/themes.json';

export interface ThemeDef {
  id: string;
  nameKey: string;
  mode: 'light' | 'dark';
  colors: Record<string, string>;
}

export const THEMES = themesFile.themes as ThemeDef[];
export const ACCENTS = themesFile.accents as string[];
const DEFAULT_THEME = themesFile.defaultTheme;
const STORAGE_KEY = 'hms.appearance';

export interface Appearance {
  theme: string; // theme id or "system"
  accent: string | null;
  density: 'compact' | 'comfortable';
}

interface ThemeContextValue extends Appearance {
  resolved: ThemeDef;
  setAppearance: (patch: Partial<Appearance>) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mix(a: string, b: string, weight: number): string {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  const m = (x: number, y: number) => Math.round(x * (1 - weight) + y * weight);
  return `#${[m(r1, r2), m(g1, g2), m(b1, b2)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Final palette: theme colors with the optional custom accent applied. */
export function paletteFor(theme: ThemeDef, accent: string | null): Record<string, string> {
  const colors = { ...theme.colors };
  if (accent && /^#[0-9a-fA-F]{6}$/.test(accent)) {
    colors.primary = accent;
    colors.onPrimary = luminance(accent) > 0.45 ? '#111111' : '#FFFFFF';
    colors.primarySoft = mix(accent, colors.surface, theme.mode === 'dark' ? 0.75 : 0.88);
    colors.chart1 = accent;
  }
  return colors;
}

function readStored(): Appearance {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (raw && typeof raw.theme === 'string')
      return { theme: raw.theme === 'opi' ? 'oph' : raw.theme, accent: raw.accent ?? null, density: raw.density === 'compact' ? 'compact' : 'comfortable' };
  } catch {
    /* ignore */
  }
  return { theme: 'system', accent: null, density: 'comfortable' };
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [appearance, setState] = useState<Appearance>(readStored);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const listener = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);

  const resolved = useMemo<ThemeDef>(() => {
    const id = appearance.theme === 'system' ? (systemDark ? 'night' : DEFAULT_THEME) : appearance.theme;
    return THEMES.find((t) => t.id === id) ?? THEMES[0];
  }, [appearance.theme, systemDark]);

  useEffect(() => {
    const root = document.documentElement;
    const colors = paletteFor(resolved, appearance.accent);
    for (const [name, value] of Object.entries(colors)) {
      root.style.setProperty(`--c-${name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`, value);
    }
    const cat = themesFile.categorical as { light: string[]; dark: string[]; previous: { light: string; dark: string } };
    cat[resolved.mode].forEach((c, i) => root.style.setProperty(`--c-cat-${i + 1}`, c));
    root.style.setProperty('--c-cat-prev', cat.previous[resolved.mode]);
    root.dataset.mode = resolved.mode;
    // Lets a theme add its own touches beyond colors (e.g. the OPH theme's gold rim).
    root.dataset.theme = resolved.id;
    root.dataset.density = appearance.density;
    root.style.colorScheme = resolved.mode;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.sidebar);
  }, [resolved, appearance.accent, appearance.density]);

  const setAppearance = useCallback((patch: Partial<Appearance>) => {
    // 'opi' was the first name of the OPH theme; keep users who picked it on it.
    if (patch.theme === 'opi') patch = { ...patch, theme: 'oph' };
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
    } catch {
      /* ignore */
    }
  }, [appearance]);

  const value = useMemo(() => ({ ...appearance, resolved, setAppearance }), [appearance, resolved, setAppearance]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
